import { useState, useEffect, useCallback, useRef } from 'react';

const CACHE_NAME = 'magazine-videos-v1';
const CACHE_EXPIRY_TIME = 24 * 60 * 60 * 1000; // 24시간
const MAX_CACHE_SIZE = 100 * 1024 * 1024; // 100MB

/**
 * 비디오 캐싱을 위한 커스텀 훅
 * @param {string} videoUrl - 캐싱할 비디오 URL
 * @param {object} options - 캐싱 옵션
 * @param {boolean} options.preload - 즉시 캐싱할지 여부
 * @param {number} options.priority - 캐싱 우선순위 (1-10, 높을수록 우선)
 * @returns {object} 캐시 상태와 제어 함수들
 */
export const useVideoCache = (videoUrl, options = {}) => {
  const { preload = false, priority = 5 } = options;
  
  const [cacheStatus, setCacheStatus] = useState({
    isLoading: false,
    isCached: false,
    error: null,
    progress: 0
  });
  
  const abortControllerRef = useRef(null);
  const cachedUrlRef = useRef(null);

  // 캐시에서 비디오 URL 확인
  const checkCache = useCallback(async (url) => {
    if (!url) return false;
    
    try {
      const cache = await caches.open(CACHE_NAME);
      const response = await cache.match(url);
      
      if (response) {
        // 캐시 만료 시간 확인
        const cacheTime = response.headers.get('x-cache-time');
        if (cacheTime) {
          const age = Date.now() - parseInt(cacheTime);
          if (age > CACHE_EXPIRY_TIME) {
            await cache.delete(url);
            return false;
          }
        }
        return true;
      }
      return false;
    } catch (error) {
      console.warn('Cache check failed:', error);
      return false;
    }
  }, []);

  // 캐시 크기 관리
  const manageCacheSize = useCallback(async () => {
    try {
      const cache = await caches.open(CACHE_NAME);
      const requests = await cache.keys();
      let totalSize = 0;
      const cacheEntries = [];

      for (const request of requests) {
        const response = await cache.match(request);
        if (response) {
          const size = parseInt(response.headers.get('content-length') || '0');
          const cacheTime = parseInt(response.headers.get('x-cache-time') || '0');
          
          cacheEntries.push({
            request,
            size,
            cacheTime,
            priority: parseInt(response.headers.get('x-cache-priority') || '5')
          });
          totalSize += size;
        }
      }

      // 캐시 크기가 초과된 경우 오래된 항목부터 삭제
      if (totalSize > MAX_CACHE_SIZE) {
        cacheEntries.sort((a, b) => {
          if (a.priority !== b.priority) {
            return a.priority - b.priority; // 낮은 우선순위부터
          }
          return a.cacheTime - b.cacheTime; // 오래된 것부터
        });

        for (const entry of cacheEntries) {
          if (totalSize <= MAX_CACHE_SIZE * 0.8) break; // 80%까지 줄이기
          
          await cache.delete(entry.request);
          totalSize -= entry.size;
        }
      }
    } catch (error) {
      console.warn('Cache management failed:', error);
    }
  }, []);

  // 비디오 캐싱 실행
  const cacheVideo = useCallback(async (url) => {
    if (!url || cacheStatus.isLoading) return;

    setCacheStatus(prev => ({ ...prev, isLoading: true, error: null, progress: 0 }));

    try {
      // 기존 요청 취소
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      // 이미 캐시되어 있는지 확인
      const isCached = await checkCache(url);
      if (isCached) {
        setCacheStatus({
          isLoading: false,
          isCached: true,
          error: null,
          progress: 100
        });
        return;
      }

      // 비디오 다운로드
      const response = await fetch(url, {
        signal: abortControllerRef.current.signal,
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch video: ${response.status}`);
      }

      const contentLength = response.headers.get('content-length');
      const total = contentLength ? parseInt(contentLength) : 0;
      let loaded = 0;

      // ReadableStream으로 진행률 추적
      const reader = response.body.getReader();
      const stream = new ReadableStream({
        start(controller) {
          function pump() {
            return reader.read().then(({ done, value }) => {
              if (done) {
                controller.close();
                return;
              }

              loaded += value.byteLength;
              if (total > 0) {
                const progress = Math.round((loaded / total) * 100);
                setCacheStatus(prev => ({ ...prev, progress }));
              }

              controller.enqueue(value);
              return pump();
            });
          }
          return pump();
        }
      });

      // 새로운 Response 객체 생성
      const clonedResponse = new Response(stream, {
        headers: {
          ...Object.fromEntries(response.headers),
          'x-cache-time': Date.now().toString(),
          'x-cache-priority': priority.toString()
        }
      });

      // 캐시에 저장
      const cache = await caches.open(CACHE_NAME);
      await cache.put(url, clonedResponse);
      
      // 캐시 크기 관리
      await manageCacheSize();

      setCacheStatus({
        isLoading: false,
        isCached: true,
        error: null,
        progress: 100
      });

    } catch (error) {
      if (error.name === 'AbortError') {
        setCacheStatus(prev => ({ ...prev, isLoading: false }));
        return;
      }

      console.error('Video caching failed:', error);
      setCacheStatus({
        isLoading: false,
        isCached: false,
        error: error.message,
        progress: 0
      });
    }
  }, [checkCache, manageCacheSize, priority, cacheStatus.isLoading]);

  // 캐시된 비디오 URL 가져오기
  const getCachedVideoUrl = useCallback(async (url) => {
    if (!url) return null;

    try {
      const cache = await caches.open(CACHE_NAME);
      const response = await cache.match(url);
      
      if (response) {
        // Blob URL 생성
        const blob = await response.blob();
        const cachedUrl = URL.createObjectURL(blob);
        
        // 이전 Blob URL 해제
        if (cachedUrlRef.current) {
          URL.revokeObjectURL(cachedUrlRef.current);
        }
        
        cachedUrlRef.current = cachedUrl;
        return cachedUrl;
      }
      
      return url; // 캐시되지 않은 경우 원본 URL 반환
    } catch (error) {
      console.warn('Failed to get cached video URL:', error);
      return url;
    }
  }, []);

  // 캐시 삭제
  const clearCache = useCallback(async (url) => {
    try {
      const cache = await caches.open(CACHE_NAME);
      await cache.delete(url);
      setCacheStatus(prev => ({ ...prev, isCached: false }));
    } catch (error) {
      console.warn('Failed to clear cache:', error);
    }
  }, []);

  // 초기 캐시 상태 확인
  useEffect(() => {
    if (videoUrl) {
      checkCache(videoUrl).then(isCached => {
        setCacheStatus(prev => ({ ...prev, isCached }));
      });
    }
  }, [videoUrl, checkCache]);

  // preload 옵션이 true인 경우 자동 캐싱
  useEffect(() => {
    if (videoUrl && preload && !cacheStatus.isCached && !cacheStatus.isLoading) {
      cacheVideo(videoUrl);
    }
  }, [videoUrl, preload, cacheStatus.isCached, cacheStatus.isLoading, cacheVideo]);

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (cachedUrlRef.current) {
        URL.revokeObjectURL(cachedUrlRef.current);
      }
    };
  }, []);

  return {
    ...cacheStatus,
    cacheVideo: () => cacheVideo(videoUrl),
    getCachedVideoUrl: () => getCachedVideoUrl(videoUrl),
    clearCache: () => clearCache(videoUrl),
  };
};

// 전역 캐시 관리 함수들
export const clearAllVideoCache = async () => {
  try {
    await caches.delete(CACHE_NAME);
  } catch (error) {
    console.warn('Failed to clear all cache:', error);
  }
};

export const getCacheInfo = async () => {
  try {
    const cache = await caches.open(CACHE_NAME);
    const requests = await cache.keys();
    let totalSize = 0;
    const cacheEntries = [];

    for (const request of requests) {
      const response = await cache.match(request);
      if (response) {
        const size = parseInt(response.headers.get('content-length') || '0');
        const cacheTime = parseInt(response.headers.get('x-cache-time') || '0');
        
        cacheEntries.push({
          url: request.url,
          size,
          cacheTime: new Date(cacheTime),
          priority: parseInt(response.headers.get('x-cache-priority') || '5')
        });
        totalSize += size;
      }
    }

    return {
      totalSize,
      totalSizeMB: Math.round(totalSize / (1024 * 1024) * 100) / 100,
      entryCount: cacheEntries.length,
      entries: cacheEntries.sort((a, b) => b.cacheTime - a.cacheTime)
    };
  } catch (error) {
    console.warn('Failed to get cache info:', error);
    return null;
  }
};